// Client for the per-user proactive-prompt cadence endpoint
// (dashoneFeedbackPromptState). The widget reads state once on mount to gate the
// proactive auto-open, and stamps events when it offers / a proactive offer is
// dismissed. Authenticated with the Firebase ID token; both calls fail soft.

import { auth } from "@dashai/lib/firebase";
import type { FeedbackPromptState, FeedbackPromptEvent } from "./feedbackPromptGate";

function endpoint(): string | null {
  const base = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL as string | undefined;
  if (!base) return null;
  return `${base.replace(/\/+$/, "")}/dashoneFeedbackPromptState`;
}

async function authHeader(): Promise<Record<string, string> | null> {
  const token = await auth.currentUser?.getIdToken();
  return token ? { Authorization: `Bearer ${token}` } : null;
}

/**
 * Fetch the caller's cadence state. Returns `null` on any failure (no URL, no
 * token, network error, non-2xx) — the gate treats null as "don't auto-open
 * this session", so a fetch failure fails toward not-nagging.
 */
export async function fetchFeedbackPromptState(): Promise<FeedbackPromptState | null> {
  const url = endpoint();
  if (!url) return null;
  try {
    const headers = await authHeader();
    if (!headers) return null;
    const res = await fetch(url, { headers });
    if (!res.ok) return null;
    const d = (await res.json()) as Partial<FeedbackPromptState>;
    return {
      last_offered_at: d.last_offered_at ?? null,
      last_dismissed_at: d.last_dismissed_at ?? null,
      last_submitted_at: d.last_submitted_at ?? null,
    };
  } catch {
    return null;
  }
}

/** Stamp a proactive-prompt event server-side. Fire-and-forget; never throws. */
export async function stampFeedbackPromptEvent(event: FeedbackPromptEvent): Promise<void> {
  const url = endpoint();
  if (!url) return;
  try {
    const headers = await authHeader();
    if (!headers) return;
    await fetch(url, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ event }),
    });
  } catch {
    // fire-and-forget
  }
}
